import React, { useEffect, useState } from "react";
import { FaCalendarAlt, FaClock } from "react-icons/fa";
import DashboardCalendar from "./DashboardCalendar";
import StudentListData from "./StudentListData";
import axiosInstance from "../../../../axios";
import { Loading } from "../../../Other/Loading";
import StudentExamResult from "./StudentExamResult";
import StudentUpcomingWebinar from "./StudentUpcomingWebinar";
import moment from "moment/moment";
import Ticker from "./Ticker";
import StudentAttendance from "./StudentAttendance";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  const d = new Date(dateStr);
  return isNaN(d) ? dateStr : d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

const StudentDashboard = () => {
  const { REACT_APP_LOGIN_API_BASEURL } = process.env;
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.users);

  const [getJustAddedCourse, setGetJustAddedCourse] = useState();
  const [fetchStatus, setFetchStatus] = useState(true);
  const [dataIndex, setDataIndex] = useState(5);
  const [progressIndex, setProgressIndex] = useState(6);
  const [isloading, setIsLoading] = useState(true);
  const [forumsInterrogate, setForumsInterrogate] = useState();
  const [studentBatches, setStudentBatches] = useState([]);

  // Exam list data for chart
  const [examResults, setExamResults] = useState([]);
  const [fetchExamStatus, setFetchExamStatus] = useState(true);

  // Hover states for inline styles
  const [hoveredCourseId, setHoveredCourseId] = useState(null);
  const [hoveredSidebarCourse, setHoveredSidebarCourse] = useState(null);
  const [hoveredShortcut, setHoveredShortcut] = useState(false);

  // Dynamic learning progress state controls
  const [activeTab, setActiveTab] = useState("all"); // "all", "active", "completed"
  const [courseSearch, setCourseSearch] = useState("");
  const [courseSort, setCourseSort] = useState("recent"); // "recent", "alpha", "progress_high", "progress_low"

  useEffect(() => {
    const fetchPurchasedCourses = async () => {
      try {
        const res = await axiosInstance.get("/course/purchased");
        setGetJustAddedCourse(res?.data?.data.data);
      } catch (error) {
        console.error("Error fetching purchased courses:", error);
      } finally {
        setFetchStatus(false);
      }
    };

    fetchPurchasedCourses();
  }, []);

  useEffect(() => {
    const fetchStudentBatches = async () => {
      const token = JSON.parse(localStorage.getItem("token"));
      if (!token) return;
      try {
        const response = await axiosInstance.get(`/student/group-batch-list`, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (response.data && !response.data.error) {
          setStudentBatches(response.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching student batches:", err);
      }
    };
    fetchStudentBatches();
  }, []);

  useEffect(() => {
    const fetchExamList = async () => {
      try {
        const res = await axiosInstance.get("/test/list");
        setExamResults(res?.data?.data?.data || []);
      } catch (error) {
        console.error("Error fetching exam list for chart:", error);
      } finally {
        setFetchExamStatus(false);
      }
    };

    fetchExamList();
  }, []);

  const coursePercentageCalutation = (course) => {
    if (!course) return 0;
    const startStr = course.startDate || course.CreatedOn || course.createdAt;
    const endStr = course.endDate;
    
    if (startStr && endStr) {
      const start = new Date(startStr);
      const end = new Date(endStr);
      const now = new Date();
      if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end > start) {
        if (now <= start) return 0;
        if (now >= end) return 100;
        return ((now - start) / (end - start)) * 100;
      }
    }
    
    const value = course.progress;
    if (!value) return 0;
    const totalDuration = value.totalSection || 0;
    const totalDurationCompleted = value.finishedSection || 0;
    const percentage = (totalDurationCompleted / totalDuration) * 100;
    return totalDuration === 0 ? 0 : percentage;
  };

  const enrichedCourses = React.useMemo(() => {
    if (!getJustAddedCourse) return [];
    return getJustAddedCourse.map((course) => {
      const matching = studentBatches.filter(
        (batch) =>
          Number(batch.courseId) === Number(course.ID) ||
          batch.courseName?.toLowerCase().trim() === course.CourseName?.toLowerCase().trim()
      );
      if (matching.length > 0) {
        const primaryBatch = matching[0];
        return {
          ...course,
          startDate: course.startDate || primaryBatch.startDate,
          endDate: course.endDate || primaryBatch.endDate,
          timing: course.timing || (primaryBatch.startTime && primaryBatch.endTime ? `${primaryBatch.startTime} – ${primaryBatch.endTime}` : primaryBatch.daysType || "Flexible"),
        };
      }
      return course;
    });
  }, [getJustAddedCourse, studentBatches]);

  const handleViewProgram = (course) => {
    const slug = slugify(course.CourseName);
    navigate(`/${slug}`, { state: { course: course } });
  };

  useEffect(() => {
    fetchUserForumPosts();
  }, []);

  const fetchUserForumPosts = async () => {
    try {
      const res = await axiosInstance.get(
        `${REACT_APP_LOGIN_API_BASEURL}/forum`
      );
      const sorted = (res?.data?.data || []).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setForumsInterrogate(sorted.length > 0 ? [sorted[0]] : []);
    } catch (error) {
      console.error("Error fetching forum posts:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Prepare data for recharts
  const chartData = examResults.map((ele, idx) => {
    const total = (ele.wrongAns || 0) + (ele.rightAns || 0);
    const score = total > 0 ? Math.round(((ele.rightAns || 0) / total) * 100) : 0;
    const quizName = ele.name || `Quiz ${idx + 1}`;
    return {
      name: quizName.length > 15 ? `${quizName.substring(0, 15)}...` : quizName,
      score: score,
      fullName: quizName,
      course: ele.CourseName || "Course",
    };
  });

  // Filter and sort purchased courses dynamically
  const filteredAndSortedCourses = React.useMemo(() => {
    if (!enrichedCourses) return [];
    
    // 1. Filter
    let result = enrichedCourses.filter((ele) => {
      const progressPct = coursePercentageCalutation(ele);
      const matchesTab = 
        activeTab === "all" ||
        (activeTab === "active" && progressPct < 100) ||
        (activeTab === "completed" && progressPct === 100);
      
      const matchesSearch = ele.CourseName?.toLowerCase().includes(courseSearch.toLowerCase());
      
      return matchesTab && matchesSearch;
    });

    // 2. Sort
    result.sort((a, b) => {
      const progressA = coursePercentageCalutation(a);
      const progressB = coursePercentageCalutation(b);
      
      if (courseSort === "alpha") {
        return (a.CourseName || "").localeCompare(b.CourseName || "");
      } else if (courseSort === "progress_high") {
        return progressB - progressA;
      } else if (courseSort === "progress_low") {
        return progressA - progressB;
      } else { // "recent"
        return new Date(b.CreatedOn || 0) - new Date(a.CreatedOn || 0);
      }
    });

    return result;
  }, [enrichedCourses, activeTab, courseSearch, courseSort]);

  // Calculate learning progress stats
  const stats = React.useMemo(() => {
    if (!enrichedCourses) return { total: 0, active: 0, completed: 0, avgProgress: 0 };
    const total = enrichedCourses.length;
    let completed = 0;
    let totalProgress = 0;
    enrichedCourses.forEach(ele => {
      const pct = coursePercentageCalutation(ele);
      totalProgress += pct;
      if (pct === 100) completed++;
    });
    const active = total - completed;
    const avgProgress = total > 0 ? totalProgress / total : 0;
    return { total, active, completed, avgProgress };
  }, [enrichedCourses]);

  return (
    <>
      <div style={{ fontFamily: "'Outfit', sans-serif", padding: "10px" }}>
        {/* Header container */}
        <div
          className="dashboard-container-new studentDashboard-Data"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "#f8fafc",
            padding: "16px 24px",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            marginBottom: "24px",
          }}
        >
          <p style={{ margin: 0, fontSize: "20px", fontWeight: "800", color: "#0f172a" }}>
            Student Hub
          </p>
          <Ticker />
        </div>

        {/* Welcome Hero Banner */}
        <div
          style={{
            background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 60%, #4338ca 100%)",
            borderRadius: "24px",
            padding: "32px",
            color: "white",
            marginBottom: "30px",
            position: "relative",
            overflow: "hidden",
            boxShadow: "0 12px 30px rgba(67, 56, 202, 0.22)",
          }}
        >
          {/* Glowing gradient background blobs */}
          <div
            style={{
              position: "absolute",
              top: "-20%",
              right: "-10%",
              width: "350px",
              height: "350px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(99, 102, 241, 0.35) 0%, rgba(99, 102, 241, 0) 70%)",
              filter: "blur(25px)",
              zIndex: 1,
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: "-30%",
              left: "20%",
              width: "250px",
              height: "250px",
              borderRadius: "50%",
              background: "radial-gradient(circle, rgba(236, 72, 153, 0.18) 0%, rgba(236, 72, 153, 0) 70%)",
              filter: "blur(20px)",
              zIndex: 1,
            }}
          />

          <div style={{ position: "relative", zIndex: 2 }}>
            <h1
              style={{
                fontSize: "32px",
                fontWeight: "800",
                margin: "0 0 10px 0",
                letterSpacing: "-0.02em",
                lineHeight: "1.2",
              }}
            >
              Welcome back, {user?.firstName || "Learner"}! ✨
            </h1>
            <p
              style={{
                fontSize: "16px",
                color: "#c7d2fe",
                margin: 0,
                fontWeight: "500",
                maxWidth: "600px",
                lineHeight: "1.5",
              }}
            >
              Ready to expand your horizon? You have {enrichedCourses?.length || 0} enrolled courses active in your profile. Keep pushing forward!
            </p>

            {/* Next Lesson Shortcut Card inside Welcome Banner */}
            {enrichedCourses && enrichedCourses.length > 0 && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  background: hoveredShortcut
                    ? "rgba(255, 255, 255, 0.14)"
                    : "rgba(255, 255, 255, 0.08)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  borderRadius: "18px",
                  padding: "16px 24px",
                  marginTop: "24px",
                  maxWidth: "520px",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  cursor: "pointer",
                  transform: hoveredShortcut ? "translateY(-3px)" : "translateY(0)",
                  boxShadow: hoveredShortcut ? "0 10px 20px rgba(0, 0, 0, 0.1)" : "none",
                }}
                onClick={() => handleViewProgram(enrichedCourses[0])}
                onMouseEnter={() => setHoveredShortcut(true)}
                onMouseLeave={() => setHoveredShortcut(false)}
              >
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.18)",
                    borderRadius: "12px",
                    width: "44px",
                    height: "44px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                  }}
                >
                  🚀
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "11px",
                      fontWeight: "700",
                      textTransform: "uppercase",
                      color: "linear-gradient(to right, #8e08fb, #fbfafb)",
                      letterSpacing: "0.08em",
                    }}
                  >
                    Quick Resume Latest
                  </p>
                  <p
                    style={{
                      margin: "2px 0 0 0",
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#ffffff",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {enrichedCourses[0].CourseName}
                  </p>
                </div>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#a5b4fc",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  Resume <span style={{ transition: "transform 0.2s", transform: hoveredShortcut ? "translateX(4px)" : "none" }}>→</span>
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Main layout */}
        <div className="dashboard-main" style={{ gap: "24px" }}>
          {/* Left Columns (Stats, Progress, Chart, Upcoming) */}
          <div className="flex-80" style={{ flexBasis: "calc(76% - 18px)", marginBottom: "0" }}>
            {/* Quick stats list */}
            <StudentListData />

            <div className="dashboard-main" style={{ flexDirection: "column", gap: "24px" }}>
              {/* Learning Progress & Chart row */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
                {/* Learning progress column */}
                <div style={{ flex: "1 1 100%", width: "100%" }}>
                  <div
                    style={{
                      background: "white",
                      borderRadius: "24px",
                      padding: "28px",
                      border: "1px solid rgba(226, 232, 240, 0.8)",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02)",
                    }}
                  >
                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", gap: "12px" }}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "18px",
                          fontWeight: "800",
                          color: "#0f172a",
                        }}
                      >
                        Your Learning Progress
                      </p>
                    </div>

                    {/* Stats Summary Panel */}
                    {!fetchStatus && enrichedCourses && enrichedCourses.length > 0 && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                          gap: "16px",
                          marginBottom: "28px",
                          background: "#f8fafc",
                          padding: "20px",
                          borderRadius: "20px",
                          border: "1px solid #f1f5f9",
                        }}
                      >
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontSize: "10px", fontWeight: "800", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total Courses</span>
                          <span style={{ fontSize: "22px", fontWeight: "900", color: "#0f172a", marginTop: "4px" }}>{stats.total}</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontSize: "10px", fontWeight: "800", color: "#3b82f6", textTransform: "uppercase", letterSpacing: "0.05em" }}>Active</span>
                          <span style={{ fontSize: "22px", fontWeight: "900", color: "#1d4ed8", marginTop: "4px" }}>{stats.active}</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontSize: "10px", fontWeight: "800", color: "#10b981", textTransform: "uppercase", letterSpacing: "0.05em" }}>Completed</span>
                          <span style={{ fontSize: "22px", fontWeight: "900", color: "#047857", marginTop: "4px" }}>{stats.completed}</span>
                        </div>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontSize: "10px", fontWeight: "800", color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.05em" }}>Avg. Completion</span>
                          <span style={{ fontSize: "22px", fontWeight: "900", color: "#4f46e5", marginTop: "4px" }}>{stats.avgProgress.toFixed(0)}%</span>
                        </div>
                      </div>
                    )}

                    {/* Filter and Sort Toolbar */}
                    {!fetchStatus && enrichedCourses && enrichedCourses.length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: "16px",
                          marginBottom: "24px",
                          paddingBottom: "16px",
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        {/* Tabs */}
                        <div style={{ display: "flex", background: "#f1f5f9", padding: "4px", borderRadius: "12px" }}>
                          {["all", "active", "completed"].map((tab) => (
                            <button
                              key={tab}
                              onClick={() => {
                                setActiveTab(tab);
                                setProgressIndex(6); // reset view count
                              }}
                              style={{
                                padding: "6px 14px",
                                border: "none",
                                borderRadius: "8px",
                                background: activeTab === tab ? "white" : "transparent",
                                color: activeTab === tab ? "#0f172a" : "#64748b",
                                fontSize: "12px",
                                fontWeight: "800",
                                cursor: "pointer",
                                boxShadow: activeTab === tab ? "0 2px 6px rgba(0,0,0,0.05)" : "none",
                                transition: "all 0.2s ease",
                                textTransform: "capitalize",
                              }}
                            >
                              {tab}
                            </button>
                          ))}
                        </div>

                        {/* Search and Sort controls */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            flexWrap: "wrap",
                            flexGrow: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          {/* Search bar */}
                          <div style={{ position: "relative", minWidth: "200px" }}>
                            <input
                              type="text"
                              placeholder="Search course..."
                              value={courseSearch}
                              onChange={(e) => {
                                setCourseSearch(e.target.value);
                                setProgressIndex(6); // reset view count
                              }}
                              style={{
                                width: "100%",
                                padding: "8px 12px 8px 32px",
                                borderRadius: "12px",
                                border: "1px solid #e2e8f0",
                                fontSize: "13px",
                                fontWeight: "600",
                                outline: "none",
                                color: "#334155",
                                transition: "border-color 0.2s",
                              }}
                            />
                            <svg
                              style={{
                                position: "absolute",
                                left: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                width: "14px",
                                height: "14px",
                                color: "#94a3b8",
                              }}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>

                          {/* Sorting select */}
                          <select
                            value={courseSort}
                            onChange={(e) => setCourseSort(e.target.value)}
                            style={{
                              padding: "8px 12px",
                              borderRadius: "12px",
                              border: "1px solid #e2e8f0",
                              fontSize: "13px",
                              fontWeight: "600",
                              outline: "none",
                              color: "#334155",
                              background: "white",
                              cursor: "pointer",
                            }}
                          >
                            <option value="recent">Recently Added</option>
                            <option value="alpha">Alphabetical</option>
                            <option value="progress_high">Highest Progress</option>
                            <option value="progress_low">Lowest Progress</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {fetchStatus && (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                          gap: "20px",
                        }}
                      >
                        {[...Array(3)].map((_, index) => (
                          <div
                            key={index}
                            style={{
                              background: "#ffffff",
                              borderRadius: "20px",
                              border: "1px solid rgba(226, 232, 240, 0.8)",
                              padding: "20px",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                              minHeight: "260px",
                            }}
                          >
                            <div>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                                <span className="skeleton" style={{ width: "70px", height: "18px", borderRadius: "8px" }}></span>
                                <span className="skeleton" style={{ width: "80px", height: "14px", borderRadius: "4px" }}></span>
                              </div>
                              <span className="skeleton" style={{ width: "90%", height: "20px", borderRadius: "4px", marginBottom: "8px", display: "block" }}></span>
                              <span className="skeleton" style={{ width: "60%", height: "20px", borderRadius: "4px", marginBottom: "16px", display: "block" }}></span>

                              <div style={{ display: "flex", gap: "6px", marginBottom: "16px" }}>
                                <span className="skeleton" style={{ width: "80px", height: "16px", borderRadius: "6px" }}></span>
                                <span className="skeleton" style={{ width: "80px", height: "16px", borderRadius: "6px" }}></span>
                              </div>
                            </div>
                            <div>
                              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                                <span className="skeleton" style={{ width: "50px", height: "14px", borderRadius: "3px" }}></span>
                                <span className="skeleton" style={{ width: "30px", height: "14px", borderRadius: "3px" }}></span>
                              </div>
                              <span className="skeleton" style={{ width: "100%", height: "8px", borderRadius: "4px", marginBottom: "16px", display: "block" }}></span>
                              <span className="skeleton" style={{ width: "100%", height: "36px", borderRadius: "12px", display: "block" }}></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {!fetchStatus && filteredAndSortedCourses && filteredAndSortedCourses.length > 0 ? (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                          gap: "20px",
                        }}
                      >
                        {filteredAndSortedCourses.slice(0, progressIndex).map((ele, index) => {
                          const progressPct = coursePercentageCalutation(ele);
                          return (
                            <div
                              key={index}
                              style={{
                                background: "#ffffff",
                                borderRadius: "20px",
                                border: "1px solid rgba(226, 232, 240, 0.8)",
                                padding: "20px",
                                boxShadow: hoveredCourseId === (ele.ID || ele.id) 
                                  ? "0 10px 25px rgba(0,0,0,0.06)" 
                                  : "0 4px 12px rgba(0,0,0,0.02)",
                                transform: hoveredCourseId === (ele.ID || ele.id) ? "translateY(-4px)" : "translateY(0)",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                position: "relative",
                              }}
                              onMouseEnter={() => setHoveredCourseId(ele.ID || ele.id)}
                              onMouseLeave={() => setHoveredCourseId(null)}
                            >
                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "14px",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      fontWeight: "700",
                                      textTransform: "uppercase",
                                      color: progressPct === 100 ? "#10b981" : "#3b82f6",
                                      background: progressPct === 100 ? "#ecfdf5" : "#eff6ff",
                                      padding: "4px 10px",
                                      borderRadius: "8px",
                                      letterSpacing: "0.02em",
                                    }}
                                  >
                                    {progressPct === 100 ? "Completed" : "Active"}
                                  </span>
                                  <span style={{ fontSize: "11px", color: "#94a3b8", fontWeight: "600" }}>
                                    {ele.CreatedOn ? moment(ele.CreatedOn).format("DD MMM YYYY") : ""}
                                  </span>
                                </div>

                                <h3
                                  style={{
                                    fontSize: "15px",
                                    fontWeight: "700",
                                    color: "#0f172a",
                                    margin: "0 0 12px 0",
                                    lineHeight: "1.4",
                                    height: "42px",
                                    overflow: "hidden",
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                  }}
                                >
                                  {ele.CourseName}
                                </h3>

                                {/* Metadata pills (Start, End, Timing) */}
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "6px",
                                    marginBottom: "16px",
                                    fontSize: "10px",
                                    fontWeight: "600",
                                    color: "#64748b",
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      background: "#f8fafc",
                                      padding: "4px 8px",
                                      borderRadius: "6px",
                                      border: "1px solid #f1f5f9",
                                    }}
                                  >
                                    <FaCalendarAlt style={{ color: "#94a3b8", fontSize: "9px" }} />
                                    <span style={{ fontSize: "8px", color: "#94a3b8", fontWeight: "800", textTransform: "uppercase" }}>Start:</span>
                                    <span style={{ color: "#334155" }}>{formatDate(ele.startDate || ele.CreatedOn || ele.createdAt)}</span>
                                  </span>
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      background: "#f8fafc",
                                      padding: "4px 8px",
                                      borderRadius: "6px",
                                      border: "1px solid #f1f5f9",
                                    }}
                                  >
                                    <FaCalendarAlt style={{ color: "#94a3b8", fontSize: "9px" }} />
                                    <span style={{ fontSize: "8px", color: "#94a3b8", fontWeight: "800", textTransform: "uppercase" }}>End:</span>
                                    <span style={{ color: "#334155" }}>{formatDate(ele.endDate)}</span>
                                  </span>
                                  <span
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      background: "#f8fafc",
                                      padding: "4px 8px",
                                      borderRadius: "6px",
                                      border: "1px solid #f1f5f9",
                                    }}
                                  >
                                    <FaClock style={{ color: "#94a3b8", fontSize: "9px" }} />
                                    <span style={{ fontSize: "8px", color: "#94a3b8", fontWeight: "800", textTransform: "uppercase" }}>Timing:</span>
                                    <span style={{ color: "#334155" }}>{ele.timing || "Flexible"}</span>
                                  </span>
                                </div>
                              </div>

                              <div>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "8px",
                                  }}
                                >
                                  <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b" }}>
                                    Progress
                                  </span>
                                  <span style={{ fontSize: "12px", fontWeight: "700", color: "#0f172a" }}>
                                    {progressPct.toFixed(0)}%
                                  </span>
                                </div>

                                <div
                                  style={{
                                    height: "8px",
                                    width: "100%",
                                    background: "#f1f5f9",
                                    borderRadius: "4px",
                                    overflow: "hidden",
                                    marginBottom: "16px",
                                  }}
                                >
                                  <div
                                    style={{
                                      height: "100%",
                                      width: `${progressPct}%`,
                                      background: progressPct === 100
                                        ? "linear-gradient(90deg, #10b981 0%, #34d399 100%)"
                                        : "linear-gradient(90deg, #3b82f6 0%, #60a5fa 100%)",
                                      borderRadius: "4px",
                                      transition: "width 0.4s ease-out",
                                    }}
                                  />
                                </div>

                                <button
                                  onClick={() => handleViewProgram(ele)}
                                  style={{
                                    width: "100%",
                                    padding: "10px",
                                    borderRadius: "12px",
                                    border: "none",
                                    background: progressPct === 100 ? "#f8fafc" : "#3b82f6",
                                    color: progressPct === 100 ? "#475569" : "white",
                                    fontSize: "13px",
                                    fontWeight: "700",
                                    cursor: "pointer",
                                    transition: "all 0.2s ease",
                                    boxShadow: progressPct === 100 ? "none" : "0 4px 10px rgba(59, 130, 246, 0.15)",
                                  }}
                                >
                                  {progressPct === 100 ? "Review Course" : "Resume Course"}
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      !fetchStatus && (
                        <div
                          style={{
                            padding: "40px",
                            textAlign: "center",
                            color: "#64748b",
                            background: "#f8fafc",
                            borderRadius: "16px",
                            border: "1px dashed #cbd5e1",
                          }}
                        >
                          {courseSearch ? `No active courses match "${courseSearch}"` : `No active courses in this tab. Explore the catalogue to get started!`}
                        </div>
                      )
                    )}

                    {!fetchStatus && filteredAndSortedCourses && filteredAndSortedCourses.length > 6 && (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          marginTop: "24px",
                        }}
                      >
                        <button
                          onClick={() =>
                            setProgressIndex(
                              progressIndex === 6 ? filteredAndSortedCourses.length : 6
                            )
                          }
                          style={{
                            padding: "8px 24px",
                            borderRadius: "30px",
                            border: "1px solid #cbd5e1",
                            background: "white",
                            color: "#475569",
                            fontSize: "13px",
                            fontWeight: "700",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            boxShadow: "0 2px 5px rgba(0,0,0,0.02)",
                          }}
                        >
                          {progressIndex === 6 ? "View All Courses" : "View Less"}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Chart & Upcoming Webinars Row */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "24px" }}>
                {/* Chart component */}
                <div style={{ flex: "1 1 58%", minWidth: "300px" }}>
                  <div
                    style={{
                      background: "white",
                      borderRadius: "24px",
                      padding: "28px",
                      border: "1px solid rgba(226, 232, 240, 0.8)",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02)",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: "0 0 4px 0",
                          fontSize: "18px",
                          fontWeight: "800",
                          color: "#0f172a",
                        }}
                      >
                        Learning Performance
                      </p>
                      <p style={{ margin: "0 0 20px 0", fontSize: "13px", color: "#64748b", fontWeight: "500" }}>
                        Trends of your quiz scores over time
                      </p>
                    </div>

                    {fetchExamStatus && (
                      <div className="skeleton" style={{ width: "100%", height: "260px", borderRadius: "16px" }} />
                    )}

                    {!fetchExamStatus && chartData.length > 0 ? (
                      <div style={{ width: "100%", height: "260px" }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart
                            data={chartData}
                            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                          >
                            <defs>
                              <linearGradient id="scoreGlow" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.25} />
                                <stop offset="95%" stopColor="#4f46e5" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis
                              dataKey="name"
                              tickLine={false}
                              axisLine={false}
                              tick={{ fill: "#64748b", fontSize: 11, fontWeight: "600" }}
                            />
                            <YAxis
                              domain={[0, 100]}
                              tickLine={false}
                              axisLine={false}
                              tick={{ fill: "#64748b", fontSize: 11, fontWeight: "600" }}
                              unit="%"
                            />
                            <Tooltip
                              contentStyle={{
                                background: "rgba(15, 23, 42, 0.95)",
                                border: "none",
                                borderRadius: "12px",
                                color: "#fff",
                                boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
                                padding: "10px 14px",
                              }}
                              itemStyle={{ color: "#818cf8", fontWeight: "700" }}
                              labelStyle={{ color: "#94a3b8", fontSize: "11px", fontWeight: "600", marginBottom: "4px" }}
                            />
                            <Area
                              type="monotone"
                              dataKey="score"
                              stroke="#4f46e5"
                              strokeWidth={3}
                              fillOpacity={1}
                              fill="url(#scoreGlow)"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      !fetchExamStatus && (
                        <div
                          style={{
                            padding: "40px",
                            textAlign: "center",
                            color: "#64748b",
                            background: "#f8fafc",
                            borderRadius: "16px",
                            border: "1px dashed #cbd5e1",
                            margin: "20px 0",
                          }}
                        >
                          No quiz results found. Complete your first quiz to see your learning performance trends!
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* Upcoming webinars column */}
                <div style={{ flex: "1 1 38%", minWidth: "260px" }}>
                  <StudentUpcomingWebinar />
                </div>
              </div>

              {/* Forum updates section */}
              {forumsInterrogate && forumsInterrogate.length > 0 && (
                <div style={{ marginTop: "10px" }}>
                  <div
                    style={{
                      background: "white",
                      borderRadius: "24px",
                      padding: "28px",
                      border: "1px solid rgba(226, 232, 240, 0.8)",
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02)",
                    }}
                  >
                    <p
                      style={{
                        margin: "0 0 20px 0",
                        fontSize: "18px",
                        fontWeight: "800",
                        color: "#0f172a",
                      }}
                    >
                      Latest Forum Discussion
                    </p>

                    {forumsInterrogate.map((post) => (
                      <div
                        key={post.id || post.forumid}
                        onClick={() => navigate(`/dashboard/student/forum/`)}
                        style={{
                          cursor: "pointer",
                          padding: "20px",
                          borderRadius: "16px",
                          background: "#f8fafc",
                          border: "1px solid #f1f5f9",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow = "0 6px 15px rgba(0, 0, 0, 0.03)";
                          e.currentTarget.style.borderColor = "#e2e8f0";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "none";
                          e.currentTarget.style.borderColor = "#f1f5f9";
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "14px",
                          }}
                        >
                          <img
                            src={post.profileImage || "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=100"}
                            alt={post.firstName}
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "50%",
                              objectFit: "cover",
                              border: "2px solid #e2e8f0",
                            }}
                          />
                          <div>
                            <div style={{ fontWeight: "700", fontSize: "14px", color: "#0f172a" }}>
                              {post.firstName} {post.lastName}
                            </div>
                            <div style={{ fontSize: "11px", color: "#94a3b8", fontWeight: "600" }}>
                              {moment(post.createdat).format("DD MMM YYYY, hh:mm A")}
                            </div>
                          </div>
                        </div>
                        <div style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", marginBottom: "6px" }}>
                          {post.title}
                        </div>
                        <p style={{ margin: "0 0 12px 0", fontSize: "14px", color: "#475569", lineHeight: "1.5" }}>
                          {post.description}
                        </p>
                        {post.medialink && (
                          <img
                            src={post.medialink}
                            alt="Forum Media"
                            style={{
                              width: "100%",
                              maxHeight: "220px",
                              objectFit: "cover",
                              borderRadius: "12px",
                              marginBottom: "12px",
                            }}
                          />
                        )}
                        <div style={{ fontSize: "12px", color: "#4f46e5", fontWeight: "700", display: "flex", alignItems: "center", gap: "4px" }}>
                          💬 {post.replies || 0} replies
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Exam result tables */}
            <div style={{ marginTop: "24px" }}>
              <StudentExamResult />
            </div>
          </div>

          {/* Right Panel (Relevant courses, Calendar) */}
          <div className="flex-20" style={{ flexBasis: "calc(24% - 6px)", marginTop: "0" }}>
            {/* Relevant Courses Card */}
            <div
              style={{
                background: "white",
                borderRadius: "24px",
                padding: "24px",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02)",
                marginBottom: "24px",
              }}
            >
              <p
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: "800",
                  color: "#0f172a",
                }}
              >
                Relevant Courses
              </p>

              {fetchStatus && (
                <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
                  <Loading />
                </div>
              )}

              {!fetchStatus && enrichedCourses && enrichedCourses.length > 0 ? (
                <div>
                  {enrichedCourses.slice(0, dataIndex).map((ele, index) => (
                    <div
                      key={index}
                      onClick={() => handleViewProgram(ele)}
                      onMouseEnter={() => setHoveredSidebarCourse(ele.id)}
                      onMouseLeave={() => setHoveredSidebarCourse(null)}
                      style={{
                        padding: "12px 14px",
                        borderBottom: "1px solid #f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        cursor: "pointer",
                        borderRadius: "12px",
                        background: hoveredSidebarCourse === ele.id ? "#f8fafc" : "transparent",
                        transition: "all 0.2s ease",
                        marginBottom: "4px",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0, paddingRight: "8px" }}>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "13px",
                            fontWeight: "700",
                            color: "#0f172a",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {ele.CourseName}
                        </p>
                        <p style={{ margin: "2px 0 0 0", fontSize: "11px", color: "#64748b", fontWeight: "600" }}>
                          {ele.instructorName || "Instructor"}
                        </p>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", flexShrink: 0 }}>
                        <span style={{ fontSize: "11px", fontWeight: "700", color: "#4f46e5" }}>
                          {ele.Duration ? `${ele.Duration}h` : ""}
                        </span>
                        <span style={{ fontSize: "11px", fontWeight: "700", color: "#0f172a", marginTop: "2px" }}>
                          {ele.EnrollmentFee || "Free"}
                        </span>
                      </div>
                    </div>
                  ))}

                  {enrichedCourses.length > 5 && (
                    <div
                      onClick={() => setDataIndex(dataIndex === 5 ? enrichedCourses.length : 5)}
                      style={{
                        textAlign: "center",
                        fontSize: "12px",
                        fontWeight: "700",
                        color: "#4f46e5",
                        cursor: "pointer",
                        marginTop: "12px",
                        padding: "6px",
                      }}
                    >
                      {dataIndex === 5 ? "View All" : "View Less"}
                    </div>
                  )}
                </div>
              ) : (
                !fetchStatus && (
                  <p style={{ margin: 0, fontSize: "13px", color: "#94a3b8", textAlign: "center" }}>
                    No courses available
                  </p>
                )
              )}
            </div>

            {/* Calendar Card */}
            <div
              style={{
                background: "white",
                borderRadius: "24px",
                padding: "24px",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.02)",
              }}
            >
              <p
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "16px",
                  fontWeight: "800",
                  color: "#0f172a",
                }}
              >
                Calendar
              </p>
              <DashboardCalendar />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentDashboard;
